import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {AfterViewInit, ChangeDetectionStrategy, Component, computed, contentChild, ContentChild, DestroyRef, input, Optional, TemplateRef, viewChild} from "@angular/core";
import {GruppeMedlemListeModell} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {MatSelect, MatSelectModule} from "@angular/material/select";
import {AvatarKomponent} from "../../avatar/avatar";
import {FormControl, ReactiveFormsModule} from "@angular/forms";
import {MatFormFieldControl} from "@angular/material/form-field";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {harSammeId, leggEntiteterTilListeDersomIkkeInkludert} from "felles-ng2/modell/modellHjelpere";
import {MatFormFieldControlProxy} from "../../../material/MatFormFieldControlProxy";
import {Subject} from "rxjs";
import {TranslateModule} from "@ngx-translate/core";
import {MatSelectVelgAlleDirektiv} from "web-ng2/delt/direktiver/matSelect--velgAlle/matSelect--velgAlle";
import {medlemmerGruppertPåPrimærRolle, MedlemsRolle, primærMedlemsrolle} from "felles-ng2/modell/medlemModellHjelpere";
import {MedlemListevisningKompaktKomponent} from "web-ng2/moduler/medlem/komponenter/medlem-listevisning-kompakt/medlem-listevisning-kompakt";
import {NgTemplateOutlet} from "@angular/common";
import {erIkkeNil} from "felles-ng2/støttefunksjoner/nilsjekk";

const debug = initDebugger('VelgMedlemmerKomponent');


/** Viser dropdown med medlemmer.
 * <pre>
 * Fungerer med Ng reactive form via ([(skjemakontroll)]="...").
 * Fungerer med <mat-form-field> fra Material.
 *
 * MERK at vi ikke implementerer ControlValueAccessor fra angular.forms, så komponenten vil ikke fungere med
 * [(ngModel)]="..." eller [formControl]="...".
 * </pre>
 */
@Component({
    selector: 'velg-medlemmer',
    templateUrl: 'velg-medlemmer.html',
    styleUrl: 'velg-medlemmer.scss',
    host: {'[class]': 'stil()'},
    providers: [{
        provide: MatFormFieldControl,
        useFactory: (komponent: VelgMedlemmerKomponent) => new MatFormFieldControlProxy(() => komponent.nedtrekksmeny(), komponent.stateChanges),
        deps: [VelgMedlemmerKomponent]
    }],
    imports: [
        ReactiveFormsModule,
        MatSelectModule,
        TranslateModule,
        AvatarKomponent,
        MatSelectVelgAlleDirektiv,
        MedlemListevisningKompaktKomponent,
        NgTemplateOutlet
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class VelgMedlemmerKomponent implements AfterViewInit {

    /** Mulighet for å angi template som skal benyttes ved rendring av medlem */
    medlemTemplate = contentChild(TemplateRef);
    matSelect = viewChild.required(MatSelect);

    medlemmer = input.required<GruppeMedlemListeModell[]>();
    skjemakontroll = input.required<FormControl<GruppeMedlemListeModell[]>>();
    /** Medlemmer som bruker ikke skal kunne endre hvorvidt er valgt eller eo. Må være et subset av {@link #medlemmer} */
    laasteMedlemmer = input<GruppeMedlemListeModell[]>([]);
    placeholder = input('');
    stil = input<'kun-avatar' | 'avatar-og-navn'>('avatar-og-navn');

    medlemmerGruppert = computed(() => this.beregnTilgjengeligeMedlemmer());
    readonly erValgt = harSammeId;

    private nedtrekksmeny = viewChild.required<MatSelect>('medlemvelger');
    private readonly stateChanges = new Subject<void>();

    constructor(private destroyRef: DestroyRef) {
    }

    ngAfterViewInit(): void {
        this.nedtrekksmeny().stateChanges
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => this.stateChanges.next());

        //på små skjermer har vi opplevd at toppen av select-panelet havner utenfor toppen av skjermen uten mulighet til å scrolle dette innholdet inn,
        //av den grunn modifiserer vi her plasseringen av panelet slik at det blir mer synlig
        //ideelt sett burde vi ikke brukt mat-select da oppførselen ved høyt mat-select-trigger innhold blir suboptimal
        let matSelectPositions = this.matSelect()._positions;
        if (erIkkeNil(matSelectPositions)) {
            matSelectPositions.unshift({originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'bottom'}); //legges til på andre plass
            matSelectPositions.unshift({originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top'}); //legges til på første plass
        } else console.warn('matSelect._positions er null, har Material APIet blitt endret siden ver 18?');
    }

    //hjelpemetoder

    private beregnTilgjengeligeMedlemmer(): [MedlemsRolle | null, MedlemOgTilgjengelighet[]][]{
        let medlemmer = this.medlemmer();
        //legg til eventuelle medlemmer som er markert som valgt, en som ikke finnes i angitt liste over tilgjengelige medlemmer
        medlemmer = leggEntiteterTilListeDersomIkkeInkludert(this.skjemakontroll().value ?? [], medlemmer);

        //grupper etter rolle
        return medlemmerGruppertPåPrimærRolle(medlemmer)
          //legg til informasjon om medlemmet er valgbart (angitt via laasteMedlemmer() )
          .map(([rolle, medlemmer]) => [rolle, this.beregnTilgjengelighet(medlemmer)])
    }

    private beregnTilgjengelighet(medlemmer: GruppeMedlemListeModell[]): MedlemOgTilgjengelighet[] {
        return medlemmer.map(medlem => ({
            medlem,
            tilgjengelig: !this.laasteMedlemmer().some(ikkeValgbar => harSammeId(medlem, ikkeValgbar))
        }));
    }
}

type MedlemOgTilgjengelighet = { medlem: GruppeMedlemListeModell; tilgjengelig: boolean; }
